import { SelectOption } from '@/components/selectValidate';
import { MOVE_FILE_COLUMN_NAME } from '@/pages/moves/shared/constants';
import { ObjectType } from '@/shared/entities/types';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { ContractItem, ContractItemKey } from './useNegotiationRelationship';

interface UseContractRulesProps {
  setValues: (values: ContractItem) => void;
  benefitsList: SelectOption[];
  providersList: SelectOption[];
  listContracts: SelectOption[];
  listCostCenters: SelectOption[];
  listPlans: SelectOption[];
  values: ContractItem;
  useErrors: ObjectType;
  setFormFormErrorFields?: Dispatch<
    SetStateAction<{
      [p: string]: string;
    }>
  >;
}

const useContractRules = ({
  benefitsList,
  providersList,
  listContracts,
  listCostCenters,
  listPlans,
  setValues,
  values,
  useErrors,
  setFormFormErrorFields,
}: UseContractRulesProps) => {
  const handleContractItemSelect = useCallback(
    (name: ContractItemKey, value: string): void => {
      const properties: ContractItemKey[] = ['providerId', 'contractId', 'costCenterId', 'planId'];

      const resetPropertiesWhenChangeKey: Record<ContractItemKey, ContractItemKey[]> = {
        benefitId: properties.slice(0),
        providerId: properties.slice(1),
        contractId: properties.slice(2),
        costCenterId: properties.slice(3),
        planId: [],
      };

      const newValues = { ...values, [name]: !value ? null : value };

      resetPropertiesWhenChangeKey[name]?.forEach((property: ContractItemKey) => {
        newValues[property] = null;
      });

      setValues(newValues);

      setFormFormErrorFields?.((current) => {
        return {
          ...current,
          [MOVE_FILE_COLUMN_NAME.CODIGO_DO_CONTRATO]: '',
          [MOVE_FILE_COLUMN_NAME.SUBFATURA_CENTRO_DE_CUSTO]: '',
          [MOVE_FILE_COLUMN_NAME.PLANO]: '',
        };
      });
    },
    [setFormFormErrorFields, setValues, values],
  );

  const isDisabledList = (list: SelectOption[]): boolean => {
    return !list.length || list.length === 1;
  };

  const isDisabledBenefitSelect = useMemo(() => {
    return isDisabledList(benefitsList);
  }, [benefitsList]);

  const isDisabledProviderSelect = useMemo(() => {
    return isDisabledList(providersList);
  }, [providersList]);

  const isDisabledContractSelect = useMemo(() => {
    return isDisabledList(listContracts) && !useErrors[MOVE_FILE_COLUMN_NAME.CODIGO_DO_CONTRATO];
  }, [listContracts, useErrors]);

  const isDisabledCostCenterSelect = useMemo(() => {
    return (
      isDisabledList(listCostCenters) && !useErrors[MOVE_FILE_COLUMN_NAME.SUBFATURA_CENTRO_DE_CUSTO]
    );
  }, [listCostCenters, useErrors]);

  const isDisabledPlanSelect = useMemo(() => {
    return isDisabledList(listPlans) && !useErrors[MOVE_FILE_COLUMN_NAME.PLANO];
  }, [listPlans, useErrors]);

  useEffect(() => {
    if (!values.benefitId && benefitsList.length === 1) {
      handleContractItemSelect('benefitId', benefitsList[0].value as string);
    }
  }, [benefitsList, benefitsList.length, handleContractItemSelect, values.benefitId]);

  useEffect(() => {
    if (!values.providerId && providersList.length === 1) {
      handleContractItemSelect('providerId', providersList[0].value as string);
    }
  }, [handleContractItemSelect, providersList, values.providerId]);

  useEffect(() => {
    if (!values.contractId && listContracts.length === 1) {
      handleContractItemSelect('contractId', listContracts[0].value as string);
    }
  }, [handleContractItemSelect, listContracts, values.contractId]);

  useEffect(() => {
    if (!values.costCenterId && listCostCenters.length === 1) {
      handleContractItemSelect('costCenterId', listCostCenters[0].value as string);
    }
  }, [handleContractItemSelect, listCostCenters, values.costCenterId]);

  useEffect(() => {
    if (!values.planId && listPlans.length === 1) {
      handleContractItemSelect('planId', listPlans[0].value as string);
    }
  }, [handleContractItemSelect, listPlans, values.planId]);

  return {
    isDisabledBenefitSelect,
    isDisabledProviderSelect,
    isDisabledContractSelect,
    isDisabledCostCenterSelect,
    isDisabledPlanSelect,
    handleContractItemSelect,
  };
};

export default useContractRules;
